// Mobile grouping of the sidebar's modules. The modules themselves (labels,
// routes, icons, sub-menus, who sees them) come from the desktop sidebar's
// config via useVisibleNav(); this file only adds the section headings.
// A module that isn't mapped here lands in "Other", so nothing goes missing
// when new modules are added to the sidebar.
import { CalendarClock, Route } from "lucide-react";
import {
  PRODUCTION_FLOW_NAV, INCOMING_INSPECTION_NAV, DESIGN_NAV, REPORTS_NAV, CRM_NAV,
} from "@/components/layout/navConfig";

export const SECTIONS = [
  { key: "overview", label: "Overview", paths: ["/", "/otd-board"] },
  { key: "hr", label: "HR", paths: ["/hr"] },
  { key: "engineering", label: "Engineering & Planning", short: "Engineering", paths: ["/items", "/design", "/routing", "/capacity", "/mrp"] },
  { key: "production", label: "Production", paths: ["/work-orders", "/production-flow", "/production-approvals", "/quality/production-ncrs", "/quality/rework-orders"] },
  { key: "quality", label: "Quality", paths: ["/quality", "/quality-inspections", "/quality/incoming-inspection", "/quality/calibration"] },
  { key: "stores", label: "Stores & Inventory", short: "Stores", paths: ["/indents", "/stock-ledger", "/stock-availability"] },
  { key: "purchase", label: "Purchase & Suppliers", short: "Purchase", paths: ["/purchase-requests", "/purchase-orders", "/auto-procurement", "/supplier-master", "/suppliers", "/goods-inward", "/goods-receipt"] },
  { key: "sales", label: "Sales & Dealers", short: "Sales", paths: ["/dealer-master", "/customers", "/crm", "/quotations", "/customer-sales-orders", "/order-status-sheet"] },
  { key: "dispatch", label: "Dispatch & Invoicing", short: "Dispatch", paths: ["/raise-transfer-order", "/sales-orders", "/proforma-invoices", "/packing-slips", "/delivery-challans", "/tax-invoices"] },
  { key: "reports", label: "Reports", paths: ["/reports", "/daily-reports", "/sales-report"] },
  { key: "admin", label: "Administration", paths: ["/users", "/user-permissions", "/tally", "/settings"] },
];
const OTHER = { key: "other", label: "Other", paths: [] };

// Sidebar entries that open a sub-menu instead of a page (same children,
// same per-role filter as the desktop sidebar).
function childrenFor(to, user) {
  switch (to) {
    case "/production-flow": return PRODUCTION_FLOW_NAV;
    case "/quality/incoming-inspection": return INCOMING_INSPECTION_NAV;
    case "/design": return DESIGN_NAV.filter((s) => !s.roles || s.roles.includes(user?.role));
    case "/reports": return REPORTS_NAV;
    case "/crm": return CRM_NAV;
    default: return null;
  }
}

// Everything this user's desktop sidebar shows, as mobile sections:
// visibleNav + Daily Reports + Sales Report + visibleAdminNav, in sidebar
// order. Daily Reports and Sales Report are written inline in the desktop
// sidebar (not in a list), so they are mirrored here with the same rules.
export function buildMobileNav({ visibleNav, visibleAdminNav, canSeeSalesReport }, user) {
  const modules = [
    ...visibleNav.map((n) => ({ ...n, children: childrenFor(n.to, user) })),
    { to: "/daily-reports", label: "Daily Reports", icon: CalendarClock, tid: "nav-daily-reports" },
    ...(canSeeSalesReport ? [{ to: "/sales-report", label: "Sales Report", icon: Route, tid: "nav-sales-report" }] : []),
    ...visibleAdminNav,
  ];
  const sectionOf = (to) => SECTIONS.find((s) => s.paths.includes(to)) || OTHER;
  const groups = [...SECTIONS, OTHER]
    .map((s) => ({ ...s, modules: modules.filter((m) => sectionOf(m.to).key === s.key) }))
    .filter((g) => g.modules.length > 0);
  return { modules, groups, sectionOf };
}

// The module (and child page) the current URL belongs to, for the
// breadcrumb. Longest matching route wins.
export function findLocation(modules, pathname) {
  const hit = (to) => (to === "/" ? pathname === "/" : pathname === to || pathname.startsWith(to + "/"));
  let best = null;
  for (const m of modules) {
    for (const c of m.children || []) {
      if (hit(c.to) && (!best || c.to.length > best.score)) best = { module: m, child: c.to === m.to ? null : c, score: c.to.length };
    }
    if (hit(m.to) && (!best || m.to.length > best.score)) best = { module: m, child: null, score: m.to.length };
  }
  return best;
}
